import Box from '@mui/material/Box';
import Hero from '../components/home/Hero';
import ContactBand from '../components/home/ContactBand';
import TrustStrip from '../components/home/TrustStrip';
import PopularPicks from '../components/home/PopularPicks';
import BuildYourOwn from '../components/home/BuildYourOwn';
import HowItWorksV5 from '../components/home/HowItWorksV5';
import Guides from '../components/home/Guides';
import WhyTrust from '../components/home/WhyTrust';
import HomeFaq from '../components/home/HomeFaq';
import Apps from '../components/home/Apps';
import { getPopularTests } from '../data/tests';
import { BLOGS } from '../data/content';
import { useGetPackagesQuery } from '../store/edosApi';
import {
  useListRecentTestsQuery,
  useListPopularTestsQuery,
  useListPopularPackagesQuery,
  useListBlogArticlesQuery,
  useListBlogCategoriesQuery,
} from '../store/siteContentApi';
import { mapApiToPackage } from '../utils/mapApiPackage';

// "Mar 2026" from either published_at (preferred) or createdAt — the meta
// line on BlogCard shows a short month+year.
const formatBlogDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

// Convert an API blog article + matching category into the shape
// BlogCard expects. The admin schema has no author field, so fall back
// to "TrustLab Editorial" rather than leaving that meta segment empty.
const mapApiArticleToCard = (article, category) => ({
  id: article._id || article.slug,
  slug: article.slug,
  icon: category?.icon || '🧪',
  bg: article.gradient
    || category?.gradient
    || 'linear-gradient(135deg, #C8EDD8, #8DCFAA)',
  // Category label on API is like "🩸 Diabetes" — strip the leading
  // emoji since BlogCard shows the icon separately inside the banner.
  category: (category?.label || '').replace(/^[^\s]+\s/, '') || 'Health',
  title: article.title,
  excerpt: article.excerpt,
  author: 'TrustLab Editorial',
  // Stored as "5 min read"; BlogCard appends " read" itself, so strip it.
  readTime: String(article.read_time || '5 min').replace(/\s*read\s*$/i, ''),
  date: formatBlogDate(article.published_at || article.createdAt),
});

const Home = () => {

  // Popular tests — admin-curated first (managed at /admin/settings/home/popular-tests).
  // If the admin hasn't picked any yet, fall back to recent booking activity
  // (most-recent unique tests, deduped on the backend), and finally to the
  // static catalogue so the section never renders empty on fresh installs.
  const { data: curatedTests = [] } = useListPopularTestsQuery();
  const { data: recentTests  = [] } = useListRecentTestsQuery(8, { skip: curatedTests.length > 0 });
  const popularTests = curatedTests.length > 0
    ? curatedTests.slice(0, 8)
    : recentTests.length > 0
      ? recentTests
      : getPopularTests().slice(0, 8);

  // Packages — admin-curated first (managed at /admin/settings/home/popular-packages).
  // If the admin hasn't picked any, fall back to the first page of EDOS
  // packages so the section never renders empty.
  const { data: curatedPackages = [] } = useListPopularPackagesQuery();
  const { data: pkgData, isLoading: pkgLoading } = useGetPackagesQuery(
    { page: 1 },
    { skip: curatedPackages.length > 0 },
  );
  const topPackages = curatedPackages.length > 0
    ? curatedPackages.slice(0, 4)
    : (pkgData?.items ?? []).slice(0, 4).map(mapApiToPackage);


  // Blog preview — same API the /blog page consumes. Take the first 3
  // active articles (backend returns them already ordered). Fall back to
  // the static BLOGS list when the admin hasn't published any yet so
  // fresh installs don't show an empty section.
  const { data: apiArticles = [] } = useListBlogArticlesQuery();
  const { data: apiCategories = [] } = useListBlogCategoriesQuery();
  const categoryBySlug = apiCategories.reduce((m, c) => {
    m[c.slug] = c;
    return m;
  }, {});
  const featuredBlogs = apiArticles.length > 0
    ? apiArticles.slice(0, 4).map((a) => mapApiArticleToCard(a, categoryBySlug[a.category_slug]))
    : BLOGS.slice(0, 4);

  return (
    <Box>
      {/* The ten sections of the v5 reference home page, in its order.
          Four components we used to render here — Offers, FindTests,
          Cities and Membership — have no section in the reference and are
          deliberately not called. Their files are untouched so they can
          be dropped back in. FindTests in particular is now redundant:
          the header's search panel does the same job.

          AiBand, DoctorsStrip and Reviews are also no longer called: the
          reference folds all three into the single <WhyTrust /> band. */}

      {/* 1 */}  <Hero />
      {/* 2 */}  <ContactBand />
      {/* 3 */}  <TrustStrip />
      {/* 4 */}  <PopularPicks
                   packages={topPackages.slice(0, 2)}
                   tests={popularTests.slice(0, 4)}
                   loading={pkgLoading}
                 />
      {/* 5 */}  <BuildYourOwn />
      {/* 6 */}  <HowItWorksV5 />
      {/* 7 */}  <Guides articles={featuredBlogs} />
      {/* 8 */}  <WhyTrust />
      {/* 9 */}  <HomeFaq />
      {/* 10 */} <Apps />
    </Box>
  );
};

export default Home;
